import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import confirm from 'modules/confirm';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { getArrayLength, getHasElementsInArray } from 'utils';
import DataRoomNdaTableProviderContext from '../../providers/DataRoomNdaTableProvider/DataRoomNdaTableProvider.context';
import { getFirstSignerName, getSignerIds } from '../../utils';
import useCallbackAsync from 'hooks/useCallbackAsync';
import ActionMenu from 'components/ActionMenu';
import BellIcon from 'icons/Bell.icon';
import useToastPositionCalculate from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/DataRoomActionMenu/hooks/useToastPositionCalculate';
import SettingsIcon from 'icons/Settings.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import DataRoomNdaOverrideStatusModal from '../DataRoomNdaOverrideStatusModal';

import './DataRoomNdaBulkActions.styles.scss';

type DataRoomNdaBulkActionsProps = {
  className?: string;
};

const DataRoomNdaBulkActions: React.FC<DataRoomNdaBulkActionsProps> = (
  props,
) => {
  const { className } = props;

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { selected, removeAll, refetch } = useContext(
    DataRoomNdaTableProviderContext,
  );
  const { popSuccess } = useContext(PopNotificationsContext);

  const [overrideStatusModal, overrideStatusModalRef] =
    useCallbackRef<OverlayRef>();

  const { t } = useTranslation();
  const { top } = useToastPositionCalculate();

  const hasSelected = getHasElementsInArray(selected);
  const selectedCount = getArrayLength(selected);

  const classes = classNames('data-room-nda-bulk-actions', className);

  const onBulkActionDone = useCallback(() => {
    removeAll();
    refetch();
  }, [removeAll, refetch]);

  const [onSendReminders, isSendingReminders] = useCallbackAsync(async () => {
    if (
      await confirm({
        title: t('bulkSendNdaRemindersTitle', { count: selectedCount }),
        content: t('bulkSendNdaRemindersDescription', {
          count: selectedCount,
          name: getFirstSignerName(selected),
        }),
        submitBtnText: t('sendReminders', { count: selectedCount }),
        cancelBtnText: t('cancel'),
      })
    ) {
      await dataRoomApi.nda.sendReminders(dataRoomId, getSignerIds(selected));

      popSuccess(
        t('bulkSendNdaRemindersSuccess', {
          count: selectedCount,
          name: getFirstSignerName(selected),
        }),
      );
      onBulkActionDone();
    }
  });

  const actions = useMemo(
    () => [
      {
        disabled: isSendingReminders,
        icon: BellIcon,
        onClick: onSendReminders,
      },
      {
        disabled: isSendingReminders,
        icon: <SettingsIcon strokeWidth={1.6} />,
        onClick: overrideStatusModal?.open,
      },
    ],
    [isSendingReminders, onSendReminders, overrideStatusModal],
  );

  return (
    <>
      {hasSelected && (
        <ActionMenu
          className={classes}
          label={t('leadsSelected', { count: selectedCount })}
          actions={actions}
          position={{ top }}
          onClose={removeAll}
        />
      )}

      <DataRoomNdaOverrideStatusModal
        ref={overrideStatusModalRef}
        dataRoomId={dataRoomId}
        signers={selected}
        onUpdated={onBulkActionDone}
      />
    </>
  );
};

export default DataRoomNdaBulkActions;
